import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONFIRM_PHRASE = "DELETE";

const ScopeLine = ({ scope }) => {
  const parts = [];
  if (scope.financial_year) parts.push(`FY ${scope.financial_year}`);
  if (scope.quarter) parts.push(scope.quarter);
  if (scope.month) parts.push(scope.month);
  const period = parts.length ? parts.join(" / ") : "All periods";
  return (
    <p className="text-sm text-slate-700">
      <strong>Client:</strong> {scope.client_name} &nbsp;·&nbsp; <strong>Scope:</strong> {period}
    </p>
  );
};

const SampleRows = ({ sample }) => {
  if (!sample?.length) return null;
  return (
    <div className="border rounded-md overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Party</th>
            <th className="px-2 py-1 text-left">Section</th>
            <th className="px-2 py-1 text-right">Amount</th>
            <th className="px-2 py-1 text-right">TDS</th>
          </tr>
        </thead>
        <tbody>
          {sample.map((row) => (
            <tr key={row.id} className="border-t">
              <td className="px-2 py-1 font-mono">{row.date}</td>
              <td className="px-2 py-1 truncate max-w-[160px]" title={row.party_name}>{row.party_name || "-"}</td>
              <td className="px-2 py-1">{row.user_section || row.section || "-"}</td>
              <td className="px-2 py-1 text-right font-mono">{formatCurrency(row.amount)}</td>
              <td className="px-2 py-1 text-right font-mono">{formatCurrency(row.tds_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const ClearTransactionsModal = ({
  open, onClose, onConfirmed,
  clientId, financialYear, quarter, month,
}) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    queueMicrotask(() => {
      setConfirmText("");
      setPreview(null);
      setLoading(true);
      const params = { client_id: clientId };
      if (financialYear && financialYear !== "all") params.financial_year = financialYear;
      if (quarter && quarter !== "all") params.quarter = quarter;
      if (month && month !== "all") params.month = month;
      axios.get(`${API}/transactions/clear-preview`, { params })
        .then((r) => setPreview(r.data))
        .catch((err) => {
          toast.error(err.response?.data?.detail || "Failed to load delete preview");
          onClose();
        })
        .finally(() => setLoading(false));
    });
  }, [open, clientId, financialYear, quarter, month, onClose]);

  const canConfirm = preview && preview.count > 0 && confirmText.trim() === CONFIRM_PHRASE && !deleting;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const params = { client_id: clientId };
      if (financialYear && financialYear !== "all") params.financial_year = financialYear;
      if (quarter && quarter !== "all") params.quarter = quarter;
      if (month && month !== "all") params.month = month;
      const r = await axios.delete(`${API}/transactions`, { params });
      toast.success(`Deleted ${r.data.deleted} transaction(s)`);
      onConfirmed();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !deleting && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Confirm Delete Transactions
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. Review the impact before confirming.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2" data-testid="clear-preview-modal">
          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading preview...
            </div>
          )}

          {!loading && preview && (
            <>
              <div className="p-3 rounded-md bg-red-50 border border-red-200 space-y-2">
                <ScopeLine scope={preview.scope} />
                <div className="grid grid-cols-3 gap-3 pt-1">
                  <div>
                    <p className="text-[10px] uppercase text-slate-500">Transactions</p>
                    <p className="text-xl font-mono font-bold text-red-700" data-testid="preview-count">
                      {preview.count.toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-500">Total Amount</p>
                    <p className="text-base font-mono font-semibold">{formatCurrency(preview.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-500">Total TDS</p>
                    <p className="text-base font-mono font-semibold text-emerald-700">
                      {formatCurrency(preview.total_tds)}
                    </p>
                  </div>
                </div>
              </div>

              {preview.count === 0 ? (
                <p className="text-sm text-slate-600">Nothing matches the current filter — nothing to delete.</p>
              ) : (
                <>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Sample (newest first)</Label>
                    <SampleRows sample={preview.sample} />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-red-700">
                      Type <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{CONFIRM_PHRASE}</span> to confirm
                    </Label>
                    <Input
                      autoFocus
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={CONFIRM_PHRASE}
                      className="mt-2 font-mono"
                      disabled={deleting}
                      data-testid="confirm-delete-input"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting} data-testid="clear-cancel-btn">
            Cancel
          </Button>
          {preview && preview.count > 0 && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!canConfirm}
              data-testid="clear-confirm-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" /> Delete {preview.count.toLocaleString("en-IN")}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
